import { MenuItem, Stack, TextField, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { DateRangeReportFilter, ReportFilterTargetFields } from '../../api/dtos';

interface Props {
    filters: DateRangeReportFilter[];
    onChange: (filters: DateRangeReportFilter[]) => void;
}

export function lastCompletedQuarterEnd(today: DateTime = DateTime.local()): DateTime {
    return today.startOf('quarter').minus({ days: 1 });
}

// Rolling mode has no stored end date, so the server resolves it again for each preview/email.
export default function SettlementReportingPeriodSelect({ filters, onChange }: Props) {
    const target = ReportFilterTargetFields.ContractSettleDate;
    const existing = filters.find(filter => filter.filterTargetField === target);
    const lastEnd = lastCompletedQuarterEnd();
    const end = existing?.endDate ? DateTime.fromISO(existing.endDate) : lastEnd;
    const financialYear = end.month >= 7 ? end.year + 1 : end.year;
    const financialQuarter = Math.floor(((end.month + 5) % 12) / 3) + 1;
    const lastFinancialYear = lastEnd.month >= 7 ? lastEnd.year + 1 : lastEnd.year;

    const update = (quarterEnd?: string) => onChange([
        ...filters.filter(filter => filter.filterTargetField !== target),
        ...(quarterEnd ? [new DateRangeReportFilter({
            ...existing,
            filterTargetField: target,
            startDate: undefined,
            endDate: quarterEnd,
            isStartInclusive: true,
            isEndInclusive: true,
        })] : []),
    ]);
    const setQuarter = (year: number, quarter: number) => {
        if (!Number.isInteger(year) || year < 1901 || year > lastFinancialYear) return;
        const nextEnd = DateTime.local(year - 1, 7, 1).plus({ months: quarter * 3 }).minus({ days: 1 });
        update((nextEnd <= lastEnd ? nextEnd : lastEnd).toISODate()!);
    };

    return <Stack spacing={2} sx={{ minWidth: 280 }}>
        <TextField select label="Reporting period" value={existing?.endDate ? 'fixed' : 'rolling'}
            onChange={event => update(event.target.value === 'fixed' ? lastEnd.toISODate()! : undefined)}>
            <MenuItem value="rolling">Last completed quarter (automatic)</MenuItem>
            <MenuItem value="fixed">Selected quarter</MenuItem>
        </TextField>
        {existing?.endDate && <Stack direction="row" spacing={2}>
            <TextField select label="Financial year ending" value={financialYear} sx={{ minWidth: 180 }}
                onChange={event => setQuarter(Number(event.target.value), financialQuarter)}>
                {Array.from({ length: lastFinancialYear - 1900 }, (_, index) => lastFinancialYear - index)
                    .map(year => <MenuItem key={year} value={year}>FY{year}</MenuItem>)}
            </TextField>
            <TextField select label="Quarter" value={financialQuarter} sx={{ minWidth: 180 }}
                onChange={event => setQuarter(financialYear, Number(event.target.value))}>
                {['Jul–Sep', 'Oct–Dec', 'Jan–Mar', 'Apr–Jun'].map((label, index) => {
                    const quarterEnd = DateTime.local(financialYear - 1, 7, 1)
                        .plus({ months: (index + 1) * 3 }).minus({ days: 1 });
                    return <MenuItem key={index} value={index + 1} disabled={quarterEnd > lastEnd}>
                        Q{index + 1}: {label}
                    </MenuItem>;
                })}
            </TextField>
        </Stack>}
        <Typography variant="body2">
            FY{financialYear}: 1 July {financialYear - 1} to {end.toFormat('d MMMM yyyy')}.
            {' '}Earlier settlements are counted separately.
        </Typography>
    </Stack>;
}
